import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Briefcase, Loader2, ChevronLeft, ChevronRight, ArrowLeft, Building,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useClients, useCreateClient, useUpdateClient } from '@/hooks/useClients';
import { ClientForm } from './ClientForm';
import type { Client, ClientStatus } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { ClientsContractsTabs } from '@/components/ClientsContractsTabs';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';

type StatusFilter = 'all' | ClientStatus;
const STATUSES: StatusFilter[] = ['all', 'active', 'on_hold', 'inactive', 'blacklisted'];

// PR-PERMS-CLIENTS MAY 12 — scope + recurring/one-time chips so NEOM contacts
// and Riyadh walk-ups can be triaged from the master list.
type ScopeFilter = 'all' | 'neom' | 'riyadh';
const SCOPES: Array<{ value: ScopeFilter; label: string }> = [
  { value: 'all',    label: 'All scopes' },
  { value: 'neom',   label: 'NEOM' },
  { value: 'riyadh', label: 'Riyadh' },
];
type RecurFilter = 'all' | 'one_time' | 'recurring';
const RECUR: Array<{ value: RecurFilter; label: string }> = [
  { value: 'all',       label: 'All' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'one_time',  label: 'One-time' },
];

export default function ClientsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [recurFilter, setRecurFilter] = useState<RecurFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  useEffect(() => {
    const tm = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(() => ({
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    projectScope: scopeFilter === 'all' ? undefined : scopeFilter,
    isOneTime: recurFilter === 'all' ? undefined : (recurFilter === 'one_time' ? 'true' as const : 'false' as const),
    page, pageSize: 25,
  }), [search, statusFilter, scopeFilter, recurFilter, page]);

  const { data, isLoading, isError, error, refetch } = useClients(params);
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };
  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <ClientsContractsTabs />

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />{t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <Briefcase className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('dashboard.modules.clients')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('clients.subtitle')}</p>
          </div>
          <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
            <Plus className="h-4 w-4" />{t('clients.addClient')}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('clients.searchPlaceholder')} className="ps-10" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                  }`}>
                  {t(`clients.filter.${s}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-border">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground me-1">Scope</span>
            {SCOPES.map((s) => (
              <button key={s.value} onClick={() => { setScopeFilter(s.value); setPage(1); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  scopeFilter === s.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                }`}>
                {s.label}
              </button>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground ms-3 me-1">Type</span>
            {RECUR.map((r) => (
              <button key={r.value} onClick={() => { setRecurFilter(r.value); setPage(1); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  recurFilter === r.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center">
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' ? t('clients.noResults') : t('clients.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' ? t('clients.tryDifferent') : t('clients.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('clients.addFirstClient')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.code')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.name')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.cr')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.contact')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.terms')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('clients.column.status')}</th>
                      <th className="text-end px-4 py-3 w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id} onClick={() => setEditing(c)} className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer">
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.code}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                            {c.nameEn}
                          </div>
                          {c.nameAr && <div className="text-xs text-muted-foreground" dir="rtl">{c.nameAr}</div>}
                          {c.city && <div className="text-[11px] text-muted-foreground mt-0.5">{c.city}{c.region ? `, ${c.region}` : ''}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.crNumber ?? '—'}</td>
                        <td className="px-4 py-3">
                          {c.primaryContactName && <div className="text-xs">{c.primaryContactName}</div>}
                          {c.primaryContactPhone && <div className="text-xs text-muted-foreground font-mono" dir="ltr">{c.primaryContactPhone}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">Net {c.paymentTermsDays}</td>
                        <td className="px-4 py-3">
                          <Badge variant={c.status === 'active' ? 'active' : c.status === 'on_hold' ? 'gold' : 'inactive'}>
                            {t(`clients.status.${c.status}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-end" onClick={(e) => e.stopPropagation()}>
                          <RowDeleteButton
                            endpoint={`/api/v1/clients/${c.id}`}
                            entityKind="client"
                            label={c.code ?? c.nameEn ?? c.id}
                            onDeleted={() => refetch()}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('common.showingRange', { from: (page - 1) * 25 + 1, to: Math.min(page * 25, total), total })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('clients.addClient')}</SheetTitle>
            <SheetDescription>{t('clients.addClientDescription')}</SheetDescription>
          </SheetHeader>
          <ClientForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('clients.create')} />
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing?.nameEn ?? ''}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{editing?.code}</SheetDescription>
          </SheetHeader>
          <ClientForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
